'use client';

import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DayPicker } from 'react-day-picker';
import { FIELD_TRIGGER as TRIGGER } from '@/lib/form-styles';
import { useAnchoredPosition } from '@/components/ui/use-anchored-position';

export interface DatePickerProps {
  name: string;
  id?: string;
  required?: boolean;
  placeholder?: string;
  /** Blocks past dates — events are always scheduled forward. */
  disablePast?: boolean;
}

/** yyyy-mm-dd → Date at noon, so time zones can never shift the day. */
function parseDay(value: string): Date | undefined {
  if (!value) return undefined;
  const parsed = Date.parse(`${value}T12:00:00`);
  return Number.isNaN(parsed) ? undefined : new Date(parsed);
}

/** Date → yyyy-mm-dd in local time (what the forms API stores). */
function formatDay(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}


/**
 * Date field matching the app's picker (shadcn Calendar over react-day-picker)
 * and this site's combobox: a button trigger, a popover calendar, and a hidden
 * input carrying `yyyy-mm-dd` so the form posts unchanged.
 */
export function DatePicker({
  name,
  id,
  required = false,
  placeholder = 'Pick a date',
  disablePast = true,
}: DatePickerProps) {
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selected = parseDay(value);
  const { popRef, pos } = useAnchoredPosition(open, triggerRef, () => setOpen(false));

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="relative">
      {/* Carries the value and the `required` rule. Hidden inputs are exempt from
          browser validation, so an empty required pick used to reach the server as
          a 422; this one is invisible but validatable, and the browser anchors its
          "Please fill out this field" bubble to the control. */}
      <input
        name={name}
        value={value}
        required={required}
        onChange={() => undefined}
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none absolute inset-0 size-full opacity-0"
      />
      <button
        ref={triggerRef}
        type="button"
        id={id}
        onClick={() => setOpen((state) => !state)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={TRIGGER}
      >
        <span className={value ? '' : 'text-brand-navy/70'}>
          {selected
            ? selected.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })
            : placeholder}
        </span>
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          className="size-4 shrink-0 text-brand-navy/70"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
        </svg>
      </button>

      {open && pos
        ? createPortal(
            <div
              ref={popRef}
              style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 70 }}
              className="rounded-xl border border-brand-navy/10 bg-white p-3 shadow-xl shadow-brand-navy/10"
            >
              <DayPicker
                mode="single"
                captionLayout="dropdown"
                startMonth={today}
                endMonth={new Date(today.getFullYear() + 3, 11)}
                selected={selected}
                defaultMonth={selected ?? today}
                {...(disablePast ? { disabled: { before: today } } : {})}
                onSelect={(date) => {
                  setValue(date ? formatDay(date) : '');
                  setOpen(false);
                }}
                className="[--rdp-accent-color:var(--color-brand-royal)] [--rdp-accent-background-color:var(--color-brand-mist)] text-sm text-brand-navy"
              />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
